/**
 * The grid itself, virtualised on both axes.
 *
 * Only the cells on screen exist. A file of five million rows and forty
 * columns is two hundred million cells; the browser gives up somewhere around
 * tens of thousands, which is why the paged version capped at 500 rows and
 * still stuttered when anything changed. Here the DOM holds roughly the
 * viewport — around forty rows by ten columns — whatever the file's size.
 *
 * Three pieces make that work:
 *
 *  - A spacer sized `rows × ROW_HEIGHT` by `columns × COLUMN_WIDTH`, so the
 *    scrollbars behave as though everything were there.
 *  - Two virtualisers deciding which row and column indices are visible.
 *  - Absolute positioning, so scrolling swaps which indices render rather than
 *    laying anything out again.
 *
 * Rows arrive in blocks fetched on demand. That is only reasonable because
 * reading row 4,000,000 costs what reading row 1 costs — the server prunes by
 * row number — so there is no penalty for scrolling into the middle of a file.
 *
 * The cell is memoised deliberately. Once a grid is virtualised the cost moves
 * from how many nodes exist to how much work each one does, and a cell that
 * rebuilds on every scroll tick undoes the virtualising.
 *
 * ## Why the selection lives here
 *
 * Because this is the only thing that knows what a cell says. Rows are fetched
 * by this component, so moving the selection with the keyboard means reading
 * the value of a cell nobody has clicked, which the page above cannot do. The
 * page is told what ended up selected; it decides what that means.
 *
 * Cells are addressed here by their position — row index and column index —
 * and not by file row number. Position is what an arrow key moves, and the
 * translation back to a row number happens once, on the way out.
 */
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Loader, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { SHEET_BLOCK, useSheetBlocks, type SheetView } from '../api/queries';
import { formatNumber } from '../theme';

/** Fixed, because virtualising needs to know where row N sits without asking. */
export const ROW_HEIGHT = 28;
const COLUMN_WIDTH = 190;
const ROW_NUMBER_WIDTH = 96;
const HEADER_HEIGHT = 34;
/** Rows rendered beyond the viewport, so a fast scroll does not show gaps. */
export const OVERSCAN = 8;
/**
 * The most cells a selection will hand upward.
 *
 * The bar above the grid counts the selected values across the whole file, and
 * a rectangle can be dragged across more cells than there is any point
 * counting. The cap is on what is reported, not on what is highlighted.
 */
const MAX_REPORTED = 5000;
/**
 * The tallest element a browser will scroll.
 *
 * Chrome clamps at 16,777,215px (2^24 - 1) and silently ignores anything more,
 * so a spacer sized for five million rows addresses only the first six hundred
 * thousand of them. Kept under the limit rather than at it, since the exact
 * figure differs between browsers.
 */
export const MAX_SCROLL_PX = 15_000_000;

/**
 * The scroll space for a file, compressed if it would outgrow the browser.
 *
 * Chrome clamps an element's height at 16,777,215px and silently ignores the
 * rest, so a spacer sized honestly for 5.6M rows addresses only the first
 * ~599,000 of them and the scrollbar bottoms out two thirds of the way down.
 * Dividing by `scale` keeps the spacer inside the limit; every position
 * derived from it multiplies back.
 *
 * `spacerHeight <= MAX_SCROLL_PX` is the invariant the whole grid rests on.
 */
export function scrollSpace(totalRows: number): { scale: number; spacerHeight: number } {
  const realHeight = Math.max(0, totalRows) * ROW_HEIGHT;
  const scale = realHeight > MAX_SCROLL_PX ? realHeight / MAX_SCROLL_PX : 1;
  return { scale, spacerHeight: realHeight / scale };
}

/** How many whole rows fit in the viewport, ignoring overscan. */
function rowsPerScreen(viewport: number): number {
  return Math.max(1, Math.floor(viewport / ROW_HEIGHT));
}

/** The furthest down the container can be scrolled. */
function maxScrollTop(totalRows: number, viewport: number): number {
  return Math.max(0, scrollSpace(totalRows).spacerHeight - viewport);
}

/** The highest row that can sit at the top and still fill the screen. */
function maxRowAtTop(totalRows: number, viewport: number): number {
  return Math.max(0, totalRows - rowsPerScreen(viewport));
}

/**
 * The rows a person can actually see, 1-based, for the "1,204 to 1,243 of
 * 5,646,071" line under the grid.
 *
 * From the row at the top edge, not from the first row rendered. Those differ
 * by the overscan, and using the rendered range put the count out by eight at
 * both ends: at the top of a file it read "9 to 26" while showing rows 1 to 25,
 * and at the bottom it stopped eight short of the last row it was displaying.
 */
export function visibleRowRange(
  rowAtTop: number,
  viewport: number,
  totalRows: number,
): { first: number; last: number } {
  if (totalRows <= 0) return { first: 0, last: 0 };
  const first = Math.min(rowAtTop, Math.max(0, totalRows - 1));
  const last = Math.min(totalRows - 1, first + rowsPerScreen(viewport) - 1);
  return { first: first + 1, last: last + 1 };
}

/**
 * Which rows a scroll position puts on screen.
 *
 * As a fraction of the way down, deliberately, rather than by converting the
 * pixel offset back into a row. The pixel conversion looks right and leaves
 * the end of a compressed file unreachable: at the bottom of the scrollbar it
 * lands `viewport * scale` pixels short, which on the 5.6M-row file is the
 * final 265 rows. A fraction always reaches both ends, and for a file too
 * small to compress the two are arithmetically identical.
 *
 * `rowAtTop` is the row against the top edge. `firstRow` is the first row
 * *rendered*, which is earlier by the overscan, and the body is shifted up by
 * the difference so the overscan sits above the viewport instead of pushing
 * everything down.
 */
export function rowsInView(
  scrollTop: number,
  viewport: number,
  _scale: number,
  totalRows: number,
): { rowAtTop: number; firstRow: number; lastRow: number } {
  const maxScroll = maxScrollTop(totalRows, viewport);
  const maxTop = maxRowAtTop(totalRows, viewport);
  const rowAtTop =
    maxScroll > 0 ? Math.min(maxTop, Math.round((scrollTop / maxScroll) * maxTop)) : 0;
  const firstRow = Math.max(0, rowAtTop - OVERSCAN);
  const lastRow = Math.min(
    Math.max(0, totalRows - 1),
    rowAtTop + rowsPerScreen(viewport) + OVERSCAN,
  );
  return { rowAtTop, firstRow, lastRow };
}

/**
 * Where to scroll so that a given row number sits at the top.
 *
 * The inverse of `rowsInView`, and it has to stay the inverse: this is how
 * "go to row" reaches row 4,000,000, which is the only honest way to get there
 * when one pixel of a compressed scrollbar is several rows.
 */
export function offsetForRow(
  row: number,
  _scale: number,
  totalRows: number,
  viewport: number,
): number {
  const maxTop = maxRowAtTop(totalRows, viewport);
  if (maxTop === 0) return 0;
  const maxScroll = maxScrollTop(totalRows, viewport);
  return Math.min(maxScroll, Math.max(0, ((row - 1) / maxTop) * maxScroll));
}

/** The blocks covering a row range. A viewport usually spans two. */
export function blocksInView(firstRow: number, lastRow: number): number[] {
  const first = Math.floor(firstRow / SHEET_BLOCK);
  const last = Math.floor(lastRow / SHEET_BLOCK);
  return Array.from({ length: last - first + 1 }, (_, i) => first + i);
}

/**
 * Those, plus the one on either side.
 *
 * A block costs the same however many rows it holds: 250 rows and 5,000 rows
 * both take about 350ms, because the time goes on reaching object storage and
 * reading the file's index, not on the rows themselves. So the delay when
 * scrolling was never transfer, it was arriving at a boundary and waiting a
 * third of a second for a request that could have been made already.
 *
 * Clamped at both ends: there is no block before the first or after the last.
 */
export function blocksToFetch(visible: number[], totalRows: number): number[] {
  if (visible.length === 0) return [];
  const lastBlock = Math.max(0, Math.ceil(totalRows / SHEET_BLOCK) - 1);
  const wanted = new Set(visible);
  wanted.add(Math.max(0, visible[0]! - 1));
  wanted.add(Math.min(lastBlock, visible[visible.length - 1]! + 1));
  return [...wanted].sort((a, b) => a - b);
}

export type GridCell = {
  rowNo: number;
  col: string;
  value: string;
  /**
   * Whether that value is an edit rather than what the file says.
   *
   * Undo needs the difference. Putting the old value back as an edit leaves
   * the cell marked as changed and counted as unsaved, when what it should be
   * is untouched.
   */
  edited: boolean;
};

/** One cell's move from one value to another, which is what undo reverses. */
export type Change = {
  rowNo: number;
  col: string;
  before: string;
  beforeEdited: boolean;
  after: string;
};

const CELL_BASE: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  height: ROW_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  padding: '0 8px',
  fontSize: 12,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  borderRight: '1px solid var(--mantine-color-default-border)',
  borderBottom: '1px solid var(--mantine-color-default-border)',
  boxSizing: 'border-box',
};

/**
 * One cell.
 *
 * Memoised on its own values, so scrolling re-renders only the cells that
 * actually came into view — the point the virtualising would otherwise give
 * back.
 */
const Cell = memo(function Cell({
  value,
  left,
  width,
  selected,
  focused,
  changed,
  editing,
  editingInitial,
  onSelect,
  onContextMenu,
  onEdit,
  onCommitEdit,
  onCancelEdit,
  onEditKey,
}: {
  value: string;
  left: number;
  width: number;
  selected: boolean;
  /** The one cell an arrow key moves from. Outlined, the way a spreadsheet does. */
  focused: boolean;
  changed: boolean;
  editing: boolean;
  /**
   * What the editor opens with, when that is not the cell's own value: typing
   * a character on a selected cell replaces it, as it does in a spreadsheet.
   */
  editingInitial?: string;
  onSelect: (e: { shift: boolean; toggle: boolean }) => void;
  /** Where the right-click landed, so a menu can open against it. */
  onContextMenu: (e: { x: number; y: number }) => void;
  onEdit: () => void;
  onCommitEdit: (v: string) => void;
  onCancelEdit: () => void;
  /** Enter and Tab commit and then move on; the grid decides where to. */
  onEditKey: (key: string, shift: boolean, value: string) => void;
}) {
  const background = selected
    ? 'var(--mantine-color-blue-light)'
    : changed
      ? 'var(--mantine-color-chartAmber-light)'
      : undefined;

  if (editing) {
    return (
      <input
        autoFocus
        defaultValue={editingInitial ?? value}
        ref={(el) => {
          // Typing a character to start an edit puts the cursor after it,
          // rather than selecting what was typed.
          if (el && editingInitial !== undefined) {
            el.setSelectionRange(el.value.length, el.value.length);
          }
        }}
        style={{
          ...CELL_BASE,
          left,
          width,
          padding: '0 6px',
          border: '2px solid var(--mantine-color-blue-6)',
          outline: 'none',
          background: 'var(--mantine-color-body)',
          font: 'inherit',
          fontSize: 12,
        }}
        onBlur={(e) => onCommitEdit(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'Tab') {
            // Handled by the grid, which knows where the next cell is. The
            // default for Tab would move browser focus out of the grid.
            e.preventDefault();
            onEditKey(e.key, e.shiftKey, e.currentTarget.value);
            return;
          }
          if (e.key === 'Escape') onCancelEdit();
          // Arrows belong to the text being typed, not to the grid.
          e.stopPropagation();
        }}
      />
    );
  }

  return (
    <div
      title={value}
      style={{
        ...CELL_BASE,
        left,
        width,
        background,
        // Drawn inside the cell so it does not shift anything, and over the
        // selection tint so the one cell being moved from stays visible in a
        // block of highlighted ones.
        outline: focused ? '2px solid var(--mantine-color-blue-6)' : undefined,
        outlineOffset: '-2px',
      }}
      onClick={(e) => onSelect({ shift: e.shiftKey, toggle: e.metaKey || e.ctrlKey })}
      onContextMenu={(e) => {
        // The browser's own menu offers nothing about this cell, and covers
        // the one that does.
        e.preventDefault();
        onContextMenu({ x: e.clientX, y: e.clientY });
      }}
      onDoubleClick={onEdit}
    >
      {value}
    </div>
  );
});

export function SheetGrid({
  sheetId,
  columns,
  totalRows,
  ready,
  version,
  view,
  editable,
  height,
  editing,
  pending,
  scrollTo,
  clearSelection,
  cellLookup,
  onSelectionChange,
  onCellContextMenu,
  onStartEdit,
  onCommitEdit,
  onCancelEdit,
  onClearCells,
  onFillDown,
  onCopyProblem,
  onUndo,
  onRedo,
  onVisibleRowsChange,
  renderHeaderCell,
}: {
  sheetId: string | null;
  columns: string[];
  totalRows: number;
  ready: boolean;
  version?: number | null;
  view: SheetView;
  editable: boolean;
  height: string;
  editing: { rowNo: number; col: string; initial?: string } | null;
  /** Typed but unsent values, laid over whatever the server returned. */
  pending: Map<string, { row_no: number; col: string; val: string }>;
  /**
   * Where to scroll, with a counter so asking for the same row twice still
   * scrolls. A bare row number cannot express "again".
   */
  scrollTo: { row: number; nth: number } | null;
  /**
   * Bumped by the page to drop the selection, which it cannot do directly
   * because the selection lives here.
   */
  clearSelection: number;
  /**
   * Filled in with a way to read a cell by position, for the page to use when
   * it needs a value it has not been handed — pasting has to know what it is
   * about to write over. Returns null for a row that has not been fetched,
   * which the caller has to mean something sensible by.
   */
  cellLookup?: React.MutableRefObject<((row: number, col: number) => GridCell | null) | null>;
  /** Everything now selected, with the values, which only this component has. */
  onSelectionChange: (cells: GridCell[]) => void;
  /**
   * A right-click on a cell, with where it landed and what it holds.
   *
   * `inSelection` is the difference between "do this to the value I clicked"
   * and "do this to the block I had already highlighted", which the menu has
   * to know before it can offer either.
   */
  onCellContextMenu?: (e: {
    x: number;
    y: number;
    col: string;
    value: string;
    inSelection: boolean;
  }) => void;
  onStartEdit: (cell: GridCell & { initial?: string }) => void;
  onCommitEdit: (change: Change) => void;
  onCancelEdit: () => void;
  /** Empty these cells, for the Delete key. */
  onClearCells: (changes: Change[]) => void;
  /** Copy the top row of the selection down over the rest, for Cmd+D. */
  onFillDown: (cells: GridCell[]) => void;
  /** Why a copy could not be made, for the page to say so. */
  onCopyProblem: (why: string) => void;
  /** One change back, and forward again. The page keeps the history. */
  onUndo: () => void;
  onRedo: () => void;
  onVisibleRowsChange: (firstRow: number, lastRow: number) => void;
  renderHeaderCell: (col: string) => React.ReactNode;
}) {
  const parent = useRef<HTMLDivElement>(null);

  /*
    Rows are positioned by hand rather than by a virtualiser, because of a
    limit no virtualiser can get around: browsers cap an element's height at
    16,777,215px. At 28px a row that is about 599,000 rows, so a spacer of the
    true height for a 5.6M-row file is silently clamped and everything past
    that point becomes unreachable — the scrollbar bottoms out two thirds of
    the way through the file. Measured, not assumed: scrolling to the end of
    this file landed on row 599,153.

    So the scroll space is compressed by a fixed ratio and the visible rows are
    drawn at natural spacing in a layer pinned to the viewport. The scrollbar
    becomes coarse on a very large file — one pixel is several rows — which is
    why "go to row" exists and why it is the honest way to reach row 4,000,000
    anyway. Nobody drags a scrollbar there.
  */
  const { scale, spacerHeight } = scrollSpace(totalRows);

  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState(600);

  useEffect(() => {
    const el = parent.current;
    if (!el) return;
    let frame = 0;
    const onScroll = () => {
      // One read per frame: a scroll fires far more often than the screen
      // refreshes, and every read here forces layout.
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        setScrollTop(el.scrollTop);
        setViewport(el.clientHeight);
      });
    };
    setViewport(el.clientHeight);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const { rowAtTop, firstRow, lastRow } = rowsInView(scrollTop, viewport, scale, totalRows);
  const rowIndices = useMemo(
    () =>
      totalRows > 0
        ? Array.from({ length: Math.max(0, lastRow - firstRow + 1) }, (_, i) => firstRow + i)
        : [],
    [firstRow, lastRow, totalRows],
  );

  const colVirtualizer = useVirtualizer({
    horizontal: true,
    count: columns.length,
    getScrollElement: () => parent.current,
    estimateSize: () => COLUMN_WIDTH,
    overscan: 3,
  });
  const virtualCols = colVirtualizer.getVirtualItems();

  // The blocks covering what is on screen. Deduplicated, because a viewport
  // usually spans two.
  const visibleBlocks = useMemo(
    () => (rowIndices.length === 0 ? [] : blocksInView(firstRow, lastRow)),
    [firstRow, lastRow, rowIndices.length],
  );

  const blocks = useMemo(
    () => blocksToFetch(visibleBlocks, totalRows),
    [visibleBlocks, totalRows],
  );

  /*
    What to fetch, once the scrolling stops.

    Dragging the scrollbar changes the blocks under it on every frame, and
    asking for each one requests rows that were off screen before they could
    arrive. Over a filtered 50M row file that meant thirty-two requests in
    flight from a single drag, each a scan, slowing each other down until the
    last took thirty seconds — nine and a half minutes of query time to look
    at one screenful.

    A tenth of a second is under the pause between a drag ending and a person
    reading, so nothing feels held back, and a flick through the file now costs
    the one place it lands rather than everywhere it passed.
  */
  const [blocksToRequest] = useDebouncedValue(blocks, 100);

  const results = useSheetBlocks(sheetId, blocksToRequest, ready, version, view);

  /**
   * Cells the server says differ from the file, for the rows on screen.
   *
   * Taken from the blocks this grid already fetched. It used to be passed in,
   * worked out from a separate one-row request, so nothing below the first row
   * was ever marked as changed however many times it had been edited.
   */
  const savedEdits = useMemo(() => {
    const marks = new Set<string>();
    for (const r of results) {
      for (const [rowNo, colIdx] of r.data?.edited ?? []) {
        const col = r.data?.columns[colIdx];
        if (col) marks.add(`${rowNo}\u0000${col}`);
      }
    }
    return marks;
  }, [results]);

  /** Row index -> its values and file row number, from whichever block holds it. */
  const rowAt = useMemo(() => {
    const byBlock = new Map<number, { rows: string[][]; row_numbers: number[] }>();
    blocks.forEach((b, i) => {
      const d = results[i]?.data;
      if (d) byBlock.set(b, { rows: d.rows, row_numbers: d.row_numbers });
    });
    return (index: number) => {
      const block = byBlock.get(Math.floor(index / SHEET_BLOCK));
      if (!block) return null;
      const within = index % SHEET_BLOCK;
      const values = block.rows[within];
      if (!values) return null;
      return { values, rowNo: block.row_numbers[within] ?? index + 1 };
    };
  }, [blocks, results]);

  // Jumping to a row: the virtualiser positions it, and the block it needs is
  // fetched because it is then on screen.
  //
  // In an effect, not during render. Scrolling is a side effect on the DOM,
  // and doing it while rendering is both a rule React does not promise to
  // honour and, in practice, too early — the scroll container has not been
  // laid out yet, so the call lands on nothing.
  useEffect(() => {
    if (scrollTo == null || !parent.current) return;
    // Straight to the compressed position for that row. Precision the
    // scrollbar cannot offer on a large file, which is the point of typing a
    // row number rather than dragging to it.
    parent.current.scrollTop = offsetForRow(scrollTo.row, scale, totalRows, viewport);
  }, [scrollTo, scale, totalRows, viewport]);

  // Report what is on screen, so the surrounding page can say "1,204–1,243 of
  // 5,646,071" without knowing anything about virtualising.
  const { first: firstVisible, last: lastVisible } = visibleRowRange(rowAtTop, viewport, totalRows);
  useEffect(() => {
    onVisibleRowsChange(firstVisible, lastVisible);
  }, [firstVisible, lastVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------- selection, and the keys that move it ---------- */

  /*
    A rectangle, held as two corners, plus whatever was picked out separately.

    Two corners rather than a list of cells, because that is what extending a
    selection means: shift moves the far corner and the near one stays put, so
    shrinking a selection back down is the same gesture as growing it. A list
    cannot express that — it only knows what is in it, not which end is which.
  */
  type Spot = { row: number; col: number };
  const [anchor, setAnchor] = useState<Spot | null>(null);
  const [focusAt, setFocusAt] = useState<Spot | null>(null);
  /*
    Where the keyboard is, readable straight away.

    State is not: two keys handled in the same tick both read the value from
    before either of them, so holding an arrow key down could move one step
    instead of many. The ref is written at the same moment as the state and is
    what the handler reads.
  */
  const focusRef = useRef<Spot | null>(null);
  /* Cells picked out one at a time with the platform's modifier key. */
  const [extra, setExtra] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (clearSelection === 0) return;
    setAnchor(null);
    setFocusAt(null);
    focusRef.current = null;
    setExtra(new Set());
  }, [clearSelection]);

  const rect = useMemo(() => {
    if (!anchor || !focusAt) return null;
    return {
      r1: Math.min(anchor.row, focusAt.row),
      r2: Math.max(anchor.row, focusAt.row),
      c1: Math.min(anchor.col, focusAt.col),
      c2: Math.max(anchor.col, focusAt.col),
    };
  }, [anchor, focusAt]);

  const isSelected = (row: number, col: number) =>
    (rect != null && row >= rect.r1 && row <= rect.r2 && col >= rect.c1 && col <= rect.c2) ||
    extra.has(`${row}\u0000${col}`);

  /** A position turned into the cell the page understands, if its rows are loaded. */
  const cellAt = (row: number, col: number): GridCell | null => {
    const r = rowAt(row);
    const name = columns[col];
    if (!r || name === undefined) return null;
    const key = `${r.rowNo}\u0000${name}`;
    const buffered = pending.get(key);
    return {
      rowNo: r.rowNo,
      col: name,
      value: buffered ? buffered.val : (r.values[col] ?? ''),
      edited: buffered !== undefined || savedEdits.has(key),
    };
  };

  // Kept current rather than captured once: which rows are loaded changes as
  // the grid is scrolled, and a stale reader would report a row as missing
  // that has since arrived.
  if (cellLookup) cellLookup.current = cellAt;

  /*
    What is selected, as cells, for whoever asked.

    Only the rows that have been fetched can be included: a rectangle can cover
    rows nobody has scrolled to, and inventing empty values for them would put
    blanks into a copy or a clear. The cap is the other half of the same
    thought.
  */
  const selectedCells = useMemo(() => {
    const out: GridCell[] = [];
    const seen = new Set<string>();
    const push = (row: number, col: number) => {
      if (out.length >= MAX_REPORTED) return;
      const key = `${row}\u0000${col}`;
      if (seen.has(key)) return;
      seen.add(key);
      const cell = cellAt(row, col);
      if (cell) out.push(cell);
    };
    if (rect) {
      for (let r = rect.r1; r <= rect.r2; r += 1) {
        for (let c = rect.c1; c <= rect.c2; c += 1) push(r, c);
      }
    }
    for (const key of extra) {
      const [r, c] = key.split('\u0000').map(Number);
      if (r !== undefined && c !== undefined) push(r, c);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rect, extra, rowAt, columns, pending]);

  /*
    Reported when the selection actually changes, not when the array holding it
    is rebuilt.

    `useQueries` hands back a new array on every render, so the rows, and
    everything derived from them, are new objects each time. Watching the array
    itself meant telling the page about a selection that had not changed, which
    re-rendered the grid, which rebuilt the array — a loop with no exit that
    hung the page rather than showing anything wrong.
  */
  const selectionSignature = selectedCells
    .map((c) => `${c.rowNo}\u0000${c.col}\u0000${c.value}`)
    .join('|');
  useEffect(() => {
    onSelectionChange(selectedCells);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionSignature]);

  /*
    Bring a position into view, moving as little as possible.

    Not the same as jumping to a row: pressing Down once should scroll by one
    row when it needs to and not at all when it does not, or the grid lurches
    under every keystroke.
  */
  function reveal(spot: Spot) {
    const el = parent.current;
    if (!el) return;
    const perScreen = Math.max(1, Math.floor(viewport / ROW_HEIGHT) - 1);
    if (spot.row < rowAtTop) {
      el.scrollTop = offsetForRow(spot.row + 1, scale, totalRows, viewport);
    } else if (spot.row > rowAtTop + perScreen - 1) {
      el.scrollTop = offsetForRow(spot.row - perScreen + 2, scale, totalRows, viewport);
    }
    const left = spot.col * COLUMN_WIDTH;
    // The row numbers sit over the left edge, so a column is only really
    // visible once it clears them.
    if (left < el.scrollLeft) {
      el.scrollLeft = left;
    } else if (left + COLUMN_WIDTH > el.scrollLeft + el.clientWidth - ROW_NUMBER_WIDTH) {
      el.scrollLeft = left + COLUMN_WIDTH - el.clientWidth + ROW_NUMBER_WIDTH;
    }
  }

  function moveTo(spot: Spot, extend: boolean) {
    const clamped = {
      row: Math.max(0, Math.min(totalRows - 1, spot.row)),
      col: Math.max(0, Math.min(columns.length - 1, spot.col)),
    };
    if (!extend) {
      setAnchor(clamped);
      setExtra(new Set());
    }
    setFocusAt(clamped);
    focusRef.current = clamped;
    reveal(clamped);
  }

  function pick(row: number, col: number, how: { shift: boolean; toggle: boolean }) {
    parent.current?.focus();
    if (how.shift && anchor) {
      setFocusAt({ row, col });
      focusRef.current = { row, col };
      return;
    }
    if (how.toggle) {
      // Added to what is already there, the way a spreadsheet's own modifier
      // key does, rather than replacing it.
      setExtra((prev) => {
        const next = new Set(prev);
        const key = `${row}\u0000${col}`;
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
      return;
    }
    setExtra(new Set());
    setAnchor({ row, col });
    setFocusAt({ row, col });
    focusRef.current = { row, col };
  }

  async function copySelection() {
    if (extra.size > 0) {
      // The same refusal a spreadsheet gives, and for the same reason: there
      // is no shape to lay scattered cells out in.
      return 'Copying needs one block of cells, not several separate ones.';
    }
    if (!rect) return null;
    const lines: string[] = [];
    for (let r = rect.r1; r <= rect.r2; r += 1) {
      const row: string[] = [];
      for (let c = rect.c1; c <= rect.c2; c += 1) {
        const cell = cellAt(r, c);
        if (!cell) return 'Scroll through those rows first, so there is something to copy.';
        row.push(cell.value);
      }
      lines.push(row.join('\t'));
    }
    // Tab separated, which is what a spreadsheet reads and what this grid's
    // own paste already expects.
    await navigator.clipboard.writeText(lines.join('\n'));
    return null;
  }

  function onGridKey(e: React.KeyboardEvent) {
    if (editing) return;
    /*
      Keys typed somewhere else are not for the grid. React passes events up
      through portals, so typing in a filter box, a column menu or any other
      pop-up that belongs to the grid arrived here as if aimed at the selected
      cell, and the letters went into the spreadsheet instead of the box.
      Anything outside the grid's own DOM, or in a field of its own, is left
      alone.
    */
    const target = e.target as HTMLElement;
    if (!parent.current?.contains(target)) return;
    if (target !== e.currentTarget && target.closest('input, textarea, select, [contenteditable="true"]')) return;
    const mod = e.metaKey || e.ctrlKey;
    const here = focusRef.current ?? { row: rowAtTop, col: 0 };

    const arrows: Record<string, Spot> = {
      ArrowUp: { row: here.row - 1, col: here.col },
      ArrowDown: { row: here.row + 1, col: here.col },
      ArrowLeft: { row: here.row, col: here.col - 1 },
      ArrowRight: { row: here.row, col: here.col + 1 },
      PageUp: { row: here.row - Math.max(1, Math.floor(viewport / ROW_HEIGHT) - 1), col: here.col },
      PageDown: { row: here.row + Math.max(1, Math.floor(viewport / ROW_HEIGHT) - 1), col: here.col },
      Home: { row: here.row, col: 0 },
      End: { row: here.row, col: columns.length - 1 },
    };
    if (arrows[e.key]) {
      e.preventDefault();
      moveTo(arrows[e.key]!, e.shiftKey);
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      moveTo({ row: here.row, col: here.col + (e.shiftKey ? -1 : 1) }, false);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const cell = cellAt(here.row, here.col);
      if (editable && cell) onStartEdit(cell);
      return;
    }
    if (e.key === 'F2') {
      e.preventDefault();
      const cell = cellAt(here.row, here.col);
      if (editable && cell) onStartEdit(cell);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setAnchor(null);
      setFocusAt(null);
      focusRef.current = null;
      setExtra(new Set());
      return;
    }
    if ((e.key === 'Delete' || e.key === 'Backspace') && editable) {
      e.preventDefault();
      onClearCells(
        selectedCells.map((c) => ({
          rowNo: c.rowNo,
          col: c.col,
          before: c.value,
          beforeEdited: c.edited,
          after: '',
        })),
      );
      return;
    }
    if (mod && (e.key === 'c' || e.key === 'C')) {
      e.preventDefault();
      void copySelection().then((problem) => {
        if (problem) onCopyProblem(problem);
      });
      return;
    }
    if (mod && (e.key === 'd' || e.key === 'D') && editable) {
      e.preventDefault();
      onFillDown(selectedCells);
      return;
    }
    if (mod && (e.key === 'z' || e.key === 'Z')) {
      e.preventDefault();
      if (e.shiftKey) onRedo();
      else onUndo();
      return;
    }
    if (mod && (e.key === 'y' || e.key === 'Y')) {
      // What redo is on Windows, and harmless everywhere else.
      e.preventDefault();
      onRedo();
      return;
    }
    if (mod && (e.key === 'a' || e.key === 'A')) {
      e.preventDefault();
      setExtra(new Set());
      setAnchor({ row: 0, col: 0 });
      const end = { row: totalRows - 1, col: columns.length - 1 };
      setFocusAt(end);
      focusRef.current = end;
      return;
    }
    /*
      Anything else that is a single character starts an edit holding it, which
      is how a spreadsheet behaves: you select a cell and type over it rather
      than opening it first. Modifiers are left alone so browser and app
      shortcuts still work.
    */
    if (editable && !mod && !e.altKey && e.key.length === 1) {
      const cell = cellAt(here.row, here.col);
      if (cell) {
        e.preventDefault();
        onStartEdit({ ...cell, initial: e.key });
      }
    }
  }

  const totalWidth = ROW_NUMBER_WIDTH + columns.length * COLUMN_WIDTH;
  // Only what is on screen. A prefetch still in flight is not the user
  // waiting for anything, and showing a spinner for it would undo the point.
  const loading = blocks.some((b, i) => visibleBlocks.includes(b) && results[i]?.isPending);
  /*
    Whether anything is actually drawn. Not the same as having rows to draw:
    the indices exist as soon as the count does, but each one is null until
    its block arrives, which is the several seconds a filter over millions of
    rows takes.
  */
  const anyRowDrawn = rowIndices.some((i) => rowAt(i) !== null);

  return (
    <div
      ref={parent}
      /*
        Focusable, so the grid receives keys at all. A table cell cannot hold
        focus, so the keys are taken here and routed by position — which is
        also why clicking a cell focuses this element.
      */
      tabIndex={0}
      onKeyDown={onGridKey}
      style={{
        height,
        overflow: 'auto',
        position: 'relative',
        border: '1px solid var(--mantine-color-default-border)',
        borderRadius: 4,
        // The focused cell is outlined instead; a ring around the whole grid
        // would say nothing about where you are in it.
        outline: 'none',
      }}
    >
      <div style={{ width: totalWidth, height: spacerHeight + HEADER_HEIGHT, position: 'relative' }}>
        {/* Header. Sticky vertically so it survives scrolling down; its cells
            are placed by the same column virtualiser as the body, so the two
            cannot drift apart. */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 3,
            height: HEADER_HEIGHT,
            background: 'var(--mantine-color-body)',
            borderBottom: '1px solid var(--mantine-color-default-border)',
          }}
        >
          <div
            style={{
              position: 'sticky',
              left: 0,
              zIndex: 4,
              width: ROW_NUMBER_WIDTH,
              height: HEADER_HEIGHT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              padding: '0 8px',
              background: 'var(--mantine-color-body)',
              borderRight: '1px solid var(--mantine-color-default-border)',
              boxSizing: 'border-box',
            }}
          >
            <Text size="xs" fw={700} c="dimmed">#</Text>
          </div>
          {virtualCols.map((vc) => (
            <div
              key={vc.key}
              style={{
                position: 'absolute',
                top: 0,
                left: ROW_NUMBER_WIDTH + vc.start,
                width: vc.size,
                height: HEADER_HEIGHT,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                padding: '0 8px',
                boxSizing: 'border-box',
                borderRight: '1px solid var(--mantine-color-default-border)',
                background: 'var(--mantine-color-body)',
              }}
            >
              {renderHeaderCell(columns[vc.index] ?? '')}
            </div>
          ))}
        </div>

        {/* Pinned to where the viewport currently is, then laid out at natural
            row spacing inside it. The spacer above only exists to give the
            scrollbar a range; nothing is positioned against it. */}
        <div
          style={{
            position: 'absolute',
            // Follows the scroll so it always sits at the top of the viewport,
            // just under the sticky header, then up by the overscan rows drawn
            // above it. Without that shift the overscan pushes everything down
            // and the last rows of a file end up below the fold.
            top: scrollTop + HEADER_HEIGHT - (rowAtTop - firstRow) * ROW_HEIGHT,
            left: 0,
            width: totalWidth,
          }}
        >
        {rowIndices.map((index) => {
          const row = rowAt(index);
          return (
            <div
              key={index}
              style={{
                position: 'absolute',
                top: (index - firstRow) * ROW_HEIGHT,
                left: 0,
                height: ROW_HEIGHT,
                width: totalWidth,
              }}
            >
              {/* The row number stays put horizontally: on a file wide enough
                  to scroll, losing track of which row you are on is what makes
                  it unreadable. */}
              <div
                style={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  width: ROW_NUMBER_WIDTH,
                  height: ROW_HEIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  padding: '0 8px',
                  boxSizing: 'border-box',
                  background: 'var(--mantine-color-body)',
                  borderRight: '1px solid var(--mantine-color-default-border)',
                  borderBottom: '1px solid var(--mantine-color-default-border)',
                  fontSize: 11,
                  color: 'var(--mantine-color-dimmed)',
                }}
              >
                {row ? formatNumber(row.rowNo) : ''}
              </div>

              {row
                ? virtualCols.map((vc) => {
                    const col = columns[vc.index] ?? '';
                    const cellKey = `${row.rowNo}\u0000${col}`;
                    const buffered = pending.get(cellKey);
                    // The value on screen: what is buffered locally, else
                    // what the server returned for this cell.
                    const shown = buffered ? buffered.val : (row.values[vc.index] ?? '');
                    return (
                      <Cell
                        key={vc.key}
                        value={shown}
                        left={ROW_NUMBER_WIDTH + vc.start}
                        width={vc.size}
                        selected={isSelected(index, vc.index)}
                        focused={focusAt?.row === index && focusAt.col === vc.index}
                        changed={buffered !== undefined || savedEdits.has(cellKey)}
                        editing={editing?.rowNo === row.rowNo && editing.col === col}
                        editingInitial={
                          editing?.rowNo === row.rowNo && editing.col === col
                            ? editing.initial
                            : undefined
                        }
                        onSelect={(how) => pick(index, vc.index, how)}
                        onContextMenu={({ x, y }) => {
                          const inSelection = isSelected(index, vc.index);
                          // Right-clicking outside the selection moves to the
                          // cell clicked, as a spreadsheet does; right-clicking
                          // inside it leaves the block alone so the menu can
                          // act on the whole thing.
                          if (!inSelection) pick(index, vc.index, { shift: false, toggle: false });
                          onCellContextMenu?.({ x, y, col, value: shown, inSelection });
                        }}
                        onEdit={() =>
                          editable &&
                          onStartEdit({
                            rowNo: row.rowNo,
                            col,
                            value: shown,
                            edited: buffered !== undefined || savedEdits.has(cellKey),
                          })
                        }
                        onCommitEdit={(v) =>
                          onCommitEdit({
                            rowNo: row.rowNo,
                            col,
                            before: shown,
                            beforeEdited:
                              buffered !== undefined || savedEdits.has(cellKey),
                            after: v,
                          })
                        }
                        onCancelEdit={onCancelEdit}
                        onEditKey={(key, shift, v) => {
                          onCommitEdit({
                            rowNo: row.rowNo,
                            col,
                            before: shown,
                            beforeEdited:
                              buffered !== undefined || savedEdits.has(cellKey),
                            after: v,
                          });
                          // Where a spreadsheet goes next: down on Enter,
                          // across on Tab, and back the other way with shift.
                          const step =
                            key === 'Enter'
                              ? { row: index + (shift ? -1 : 1), col: vc.index }
                              : { row: index, col: vc.index + (shift ? -1 : 1) };
                          moveTo(step, false);
                          parent.current?.focus();
                        }}
                      />
                    );
                  })
                : null}
            </div>
          );
        })}
        </div>
      </div>

      {/*
        What an empty grid means.

        A filter over millions of rows takes seconds to come back, and until it
        does there is nothing to draw. A spinner in the corner is not enough to
        say so: the grid reads as "no matches" while it is still counting, and
        as broken when there genuinely are none. Both cases now say which one
        they are, in the middle of the space they are explaining.
      */}
      {totalRows === 0 && !loading && (
        <div
          style={{
            position: 'absolute',
            top: scrollTop + HEADER_HEIGHT + 48,
            left: 0,
            right: 0,
            textAlign: 'center',
            zIndex: 4,
          }}
        >
          <Text size="sm" c="dimmed">
            No rows match the filters.
          </Text>
        </div>
      )}

      {loading && !anyRowDrawn && (
        <div
          style={{
            position: 'absolute',
            top: scrollTop + HEADER_HEIGHT + 48,
            left: 0,
            right: 0,
            textAlign: 'center',
            zIndex: 4,
          }}
        >
          <Loader size="sm" />
          <Text size="sm" c="dimmed" mt={8}>
            Finding rows…
          </Text>
        </div>
      )}

      {/* The quieter one, for a refresh that still has rows on screen. */}
      {loading && anyRowDrawn && (
        <div style={{ position: 'sticky', bottom: 8, left: 8, width: 'fit-content', zIndex: 5 }}>
          <Loader size="xs" />
        </div>
      )}
    </div>
  );
}
